/**
 * NCR Management Page
 * Non-Conformance Report tracking and management
 * 
 * AS9100D Compliance:
 * - 8.7: Control of Nonconforming Outputs
 * - 10.2: Nonconformity and Corrective Action
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Filter,
  FileText,
  ClipboardCheck,
  Package,
  ArrowRight,
  RefreshCw,
  X,
  ChevronRight,
  Shield,
  User,
  Calendar,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../services/api';
import InventoryLayout from '../components/layouts/InventoryLayout';
import { formatIST } from '../utils/timezone';

// Status configurations
const STATUS_CONFIG = {
  OPEN: { label: 'Open', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  CLOSED: { label: 'Closed', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
};

const SEVERITY_CONFIG = {
  CRITICAL: { label: 'Critical', color: 'bg-red-600 text-white', icon: AlertTriangle },
  MAJOR: { label: 'Major', color: 'bg-orange-500 text-white', icon: AlertCircle },
  MINOR: { label: 'Minor', color: 'bg-yellow-500 text-white', icon: AlertCircle },
};

const TYPE_CONFIG = {
  INSPECTION_REJECTION: { label: 'QC Inspection Rejection', icon: ClipboardCheck },
  INVENTORY_VARIANCE: { label: 'Inventory Variance', icon: Package },
  CUSTOMER_COMPLAINT: { label: 'Customer Complaint', icon: AlertTriangle },
  SUPPLIER_ISSUE: { label: 'Supplier Issue', icon: AlertTriangle },
};

/**
 * NCR Summary Cards
 */
const NCRSummaryCards = ({ summary }) => {
  if (!summary) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Open NCRs</p>
              <p className="text-2xl font-bold text-red-700">{summary.total_open}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600">In Progress</p>
              <p className="text-2xl font-bold text-amber-700">{summary.total_in_progress}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Closed</p>
              <p className="text-2xl font-bold text-green-700">{summary.total_closed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </CardContent>
      </Card>
      
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600">Critical/Major</p>
              <p className="text-2xl font-bold text-purple-700">
                {(summary.by_severity?.critical || 0) + (summary.by_severity?.major || 0)}
              </p>
            </div>
            <AlertTriangle className="w-8 h-8 text-purple-400" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * NCR Detail Dialog
 */
const NCRDetailDialog = ({ ncr, open, onOpenChange, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    root_cause: '',
    corrective_action: '',
    preventive_action: '',
    closure_notes: '',
    status: ''
  });

  useEffect(() => {
    if (ncr) {
      setFormData({
        root_cause: ncr.root_cause || '',
        corrective_action: ncr.corrective_action || '',
        preventive_action: ncr.preventive_action || '',
        closure_notes: ncr.closure_notes || '',
        status: ncr.status || 'OPEN'
      });
    }
  }, [ncr]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.patch(`/api/v1/inventory/v2/ncrs/${ncr.ncr_id}`, formData);
      toast.success('NCR updated successfully');
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update NCR');
    } finally {
      setLoading(false);
    }
  };

  if (!ncr) return null;

  const severityConfig = SEVERITY_CONFIG[ncr.severity] || SEVERITY_CONFIG.MINOR;
  const statusConfig = STATUS_CONFIG[ncr.status] || STATUS_CONFIG.OPEN;
  const typeConfig = TYPE_CONFIG[ncr.type] || { label: ncr.type, icon: AlertTriangle };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <span>{ncr.ncr_number}</span>
            <Badge className={severityConfig.color}>{severityConfig.label}</Badge>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            AS9100D 8.7 & 10.2 - Nonconformity Management
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* NCR Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Type</p>
              <div className="flex items-center gap-2">
                <typeConfig.icon className="w-4 h-4 text-gray-600" />
                <p className="font-medium">{typeConfig.label}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <Badge variant="outline" className={statusConfig.color}>
                {statusConfig.label}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-500">Part</p>
              <p className="font-medium">{ncr.hpn_display || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Lot</p>
              <p className="font-mono">{ncr.lot_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Quantity Affected</p>
              <p className="font-medium">{ncr.quantity_affected}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="text-sm">{formatIST(ncr.created_at)}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-gray-500">Description</Label>
            <p className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              {ncr.description}
            </p>
          </div>

          {/* Traceability Links */}
          <div className="flex flex-wrap gap-2">
            {ncr.quarantine_id && (
              <Button variant="outline" size="sm" className="text-purple-600" asChild>
                <a href={`/inventory/quarantine?id=${ncr.quarantine_id}`}>
                  <Package className="w-4 h-4 mr-1" />
                  View Quarantine
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            )}
            {ncr.grn_id && (
              <Button variant="outline" size="sm" className="text-blue-600" asChild>
                <a href={`/inventory/receiving?grn=${ncr.grn_id}`}>
                  <FileText className="w-4 h-4 mr-1" />
                  View GRN
                  <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
            )}
          </div>

          <Separator />

          {/* Corrective Action Form */}
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-600" />
              Corrective Action (AS9100D 10.2)
            </h4>

            <div className="space-y-2">
              <Label htmlFor="root_cause">Root Cause Analysis</Label>
              <Textarea
                id="root_cause"
                placeholder="Identify the root cause of the nonconformity..."
                value={formData.root_cause}
                onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="corrective_action">Corrective Action</Label>
              <Textarea
                id="corrective_action"
                placeholder="Describe the corrective action taken..."
                value={formData.corrective_action}
                onChange={(e) => setFormData({ ...formData, corrective_action: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preventive_action">Preventive Action</Label>
              <Textarea
                id="preventive_action"
                placeholder="Describe actions to prevent recurrence..."
                value={formData.preventive_action}
                onChange={(e) => setFormData({ ...formData, preventive_action: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.status === 'CLOSED' && (
                <div className="space-y-2">
                  <Label htmlFor="closure_notes">Closure Notes</Label>
                  <Input
                    id="closure_notes"
                    placeholder="Closure verification notes"
                    value={formData.closure_notes}
                    onChange={(e) => setFormData({ ...formData, closure_notes: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main NCR Management Page
 */
const NCRManagementPage = () => {
  const [ncrs, setNcrs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNCR, setSelectedNCR] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchNCRs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (severityFilter) params.append('severity', severityFilter);
      params.append('page', page);
      params.append('page_size', '20');

      const response = await api.get(`/api/v1/inventory/v2/ncrs?${params.toString()}`);
      setNcrs(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching NCRs:', error);
      toast.error('Failed to load NCRs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, page]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/inventory/v2/ncrs/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching NCR summary:', error);
    }
  }, []);

  useEffect(() => {
    fetchNCRs();
    fetchSummary();
  }, [fetchNCRs, fetchSummary]);

  const handleViewNCR = async (ncr) => {
    try {
      const response = await api.get(`/api/v1/inventory/v2/ncrs/${ncr.ncr_id}`);
      setSelectedNCR(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to load NCR details');
    }
  };

  const handleRefresh = () => {
    fetchNCRs();
    fetchSummary();
  };

  // Filter NCRs by search term
  const filteredNCRs = ncrs.filter(ncr => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ncr.ncr_number?.toLowerCase().includes(search) ||
      ncr.hpn_display?.toLowerCase().includes(search) ||
      ncr.lot_number?.toLowerCase().includes(search) ||
      ncr.description?.toLowerCase().includes(search)
    );
  });

  return (
    <InventoryLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-red-600" />
              NCR Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Non-Conformance Reports • AS9100D 8.7 & 10.2
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <NCRSummaryCards summary={summary} />

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search NCR, part, lot..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="w-40">
                <Label className="text-xs text-gray-500">Status</Label>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <Label className="text-xs text-gray-500">Severity</Label>
                <Select value={severityFilter || "all"} onValueChange={(v) => setSeverityFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Severities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                    <SelectItem value="MAJOR">Major</SelectItem>
                    <SelectItem value="MINOR">Minor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(statusFilter || severityFilter || searchTerm) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('');
                    setSeverityFilter('');
                    setSearchTerm('');
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* NCR Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredNCRs.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                <CheckCircle className="w-12 h-12 mb-4 text-green-400" />
                <p className="text-lg font-medium">No NCRs Found</p>
                <p className="text-sm">No non-conformance reports match your filters</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>NCR #</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Part / Lot</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredNCRs.map((ncr) => {
                    const severityConfig = SEVERITY_CONFIG[ncr.severity] || SEVERITY_CONFIG.MINOR;
                    const statusConfig = STATUS_CONFIG[ncr.status] || STATUS_CONFIG.OPEN;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <TableRow 
                        key={ncr.ncr_id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleViewNCR(ncr)}
                        data-testid={`ncr-row-${ncr.ncr_id}`}
                      >
                        <TableCell>
                          <span className="font-mono font-medium text-red-600">
                            {ncr.ncr_number}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${severityConfig.color} text-xs`}>
                            {severityConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {TYPE_CONFIG[ncr.type]?.label || ncr.type}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{ncr.hpn_display || 'N/A'}</p>
                            <p className="text-xs text-gray-500 font-mono">{ncr.lot_number || '-'}</p>
                          </div>
                        </TableCell>
                        <TableCell>{ncr.quantity_affected}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`${statusConfig.color} text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500">
                            {formatIST(ncr.created_at, 'short')}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}

        {/* NCR Detail Dialog */}
        <NCRDetailDialog
          ncr={selectedNCR}
          open={showDetail}
          onOpenChange={setShowDetail}
          onUpdate={handleRefresh}
        />
      </div>
    </InventoryLayout>
  );
};

export default NCRManagementPage;
